import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../theme/getTheme'

const MENU_MAX_H = 260

function menuPanelBackground(theme: Theme): string {
  return theme.background.cardElevated ?? theme.background.card ?? '#FFFFFF'
}

export function getSeatBucketPickerStyles(theme: Theme) {
  const menuBg = menuPanelBackground(theme)

  return StyleSheet.create({
    container: {
      marginTop: 28,
      marginBottom: 28,
    },
    /**
     * Variant of `container` used when paired with a sibling control on the same row
     * (e.g. an Upgrade button). Drops the vertical rhythm and stretches to fill
     * the flex cell so the trigger matches its sibling's height.
     */
    containerInlineRow: {
      marginTop: 0,
      marginBottom: 0,
      flex: 1,
      alignSelf: 'stretch' as const,
    },
    /** Wrapper inside an inline-row container so the trigger fills the cell. */
    triggerInlineRowWrap: {
      flex: 1,
      alignSelf: 'stretch' as const,
    },
    /** Rotated chevron when the menu is open (web only — native uses fade). */
    triggerChevronOpen: {
      transform: [{ rotate: '180deg' }],
    },
    label: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 16,
    },
    trigger: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      backgroundColor: theme.background.card ?? theme.white,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: theme.gray[400],
      paddingHorizontal: 16,
      paddingVertical: 14,
      minHeight: 52,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer' as const,
      }),
    },
    /** Fills a flex row cell next to another control (same height as sibling). */
    triggerInlineRow: {
      flex: 1,
      alignSelf: 'stretch' as const,
      minHeight: theme.touchTargets.minHeight,
      paddingVertical: theme.spacing.sm,
    },
    triggerPressed: {
      opacity: 0.92,
    },
    triggerHovered: {
      borderColor: theme.gray[500],
    },
    triggerDisabled: {
      opacity: 0.55,
    },
    triggerText: {
      flex: 1,
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 15,
      fontWeight: '600',
      color: theme.gray[900],
      paddingRight: 8,
    },
    triggerChevron: {
      flexShrink: 0,
      ...(Platform.OS === 'web' && {
        transition: 'transform 0.15s ease-out' as const,
      }),
    },
    /** Full-screen transparent backdrop — captures outside taps to close. */
    modalBackdrop: {
      flex: 1,
    },
    /**
     * Rendered inside a Modal so it lives at the root of the React tree.
     * No parent overflow or stacking context can affect it, which is why the
     * background is always fully opaque (unlike absolutely-positioned overlays
     * anchored inside the pricing card).
     * top / left / width are injected as inline style from measure().
     */
    menu: {
      position: 'absolute' as const,
      maxHeight: MENU_MAX_H,
      backgroundColor: menuBg,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: theme.gray[500],
      overflow: 'hidden' as const,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)',
      }),
    },
    menuScroll: {
      maxHeight: MENU_MAX_H,
      backgroundColor: menuBg,
    },
    menuScrollWeb: {
      overflow: 'scroll' as const,
      maxHeight: MENU_MAX_H,
      backgroundColor: menuBg,
      WebkitOverflowScrolling: 'touch',
    } as any,
    menuScrollContent: {
      flexGrow: 1,
      paddingVertical: 4,
      backgroundColor: menuBg,
    },
    menuItem: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      paddingVertical: 12,
      paddingHorizontal: 14,
      gap: 10,
      backgroundColor: menuBg,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.gray[200],
    },
    menuItemLast: {
      borderBottomWidth: 0,
    },
    menuItemSelected: {
      backgroundColor: menuBg,
      borderLeftWidth: 3,
      borderLeftColor: theme.primary,
    },
    menuItemPressed: {
      opacity: 0.9,
    },
    menuItemHovered: {
      backgroundColor: theme.gray[100],
    },
    menuItemText: {
      flex: 1,
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 15,
      lineHeight: 20,
      color: theme.gray[800],
    },
    menuItemTextSelected: {
      fontWeight: '700',
      color: theme.gray[900],
    },
    menuItemCheckSpacer: {
      width: 18,
      height: 18,
    },
  })
}
